import { type MantineColorSchemeManager, useMantineColorScheme } from '@mantine/core';
import { useEffect } from 'react';

import { useAuth } from './auth-context';

const LS_KEY = 'mantine-color-scheme-value';

export type ColorSchemePreference = 'light' | 'dark' | 'system';

// The app default is DARK — not the browser's prefers-color-scheme. Only an
// explicit "system" choice follows the device. So absence of a stored value
// resolves to 'dark', while 'system' stays a distinct, browser-following choice.
export function getStoredColorScheme(): ColorSchemePreference {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem(LS_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'dark';
}

function systemScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve a stored preference to a CONCRETE scheme. We deliberately never hand
 * Mantine "auto": the warm dark-mode + ink-inversion CSS is keyed on a concrete
 * `[data-mantine-color-scheme="dark"]` / `"light"` attribute, so an "auto"
 * attribute would switch all of it OFF after the first paint (the FOUC / flash).
 * Keeping the attribute concrete means every warmth rule applies on first paint
 * and never flips. `system` is resolved here and tracked live (see the listener
 * in useColorSchemeSync), so the System preference still follows the OS.
 */
export function resolveConcreteScheme(
  pref: ColorSchemePreference = getStoredColorScheme(),
): 'light' | 'dark' {
  return pref === 'system' ? systemScheme() : pref;
}

/**
 * A Mantine colour-scheme manager that defers entirely to the app's stored
 * preference (resolved to concrete). Mantine never persists its own value, so it
 * cannot clobber the 'system' preference and the attribute stays concrete —
 * matching what the index.html inline bridge already painted, so there is no
 * scheme flip on mount.
 */
export const appColorSchemeManager: MantineColorSchemeManager = {
  get: () => resolveConcreteScheme(),
  set: () => {
    /* The app owns persistence via apply() / useColorSchemeSync; Mantine does not. */
  },
  subscribe: () => {},
  unsubscribe: () => {},
  clear: () => {},
};

export function useColorSchemeSync() {
  const { user } = useAuth();
  const { setColorScheme } = useMantineColorScheme();

  // Sync the server-stored preference -> local + Mantine (resolved to concrete).
  // Skip if already in sync to avoid a needless re-apply.
  useEffect(() => {
    if (!user) return;
    // Null/unset server pref => the app default (dark), never browser-following.
    const serverPref = user.colorSchemePreference ?? 'dark';
    if (localStorage.getItem(LS_KEY) === serverPref) return;
    localStorage.setItem(LS_KEY, serverPref);
    setColorScheme(resolveConcreteScheme(serverPref));
  }, [user, setColorScheme]);

  // When the preference is 'system', follow live OS theme changes. (We resolve to
  // concrete rather than using Mantine "auto", so this listener is what keeps
  // System tracking the device.)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (getStoredColorScheme() === 'system') {
        setColorScheme(mq.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [setColorScheme]);
}

export function useColorSchemePreference() {
  const { setColorScheme } = useMantineColorScheme();

  const apply = (pref: ColorSchemePreference) => {
    localStorage.setItem(LS_KEY, pref);
    setColorScheme(resolveConcreteScheme(pref));
  };

  return { apply };
}
